import { Box, Button, Card, CardHeader, Layer, Tab, Tabs, Text } from 'grommet';
import { Channel, Close, Deploy, Globe, Magic, Multimedia, User } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import {
  useCommercialStore,
  useProjectStore,
  useRoleStore,
  useSessionStore,
  useUserStore,
} from '@talon/web/src/context';
import { toast } from 'react-toastify';
import { ProjectWizardFooter } from './ProjectWizardFooter';

import { Advertiser, CommercialType, LocationType } from '@talon/web/lib/api';
import { borderRadiusSize } from 'src/theme';
import { toDateOnly } from 'src/utils/dateOnly';
import {
  AssetsTab,
  IRolePerformer,
  MediaTab,
  RolePerformersTab,
  ShootTab,
  StartTab,
  SummaryTab,
  defaultRolePerformer,
  formatDefaultName,
} from '.';
import { Loader } from '..';
import { IAddress } from '../../../utils/decodeGoogle';

const STEPS = 5;

export type IMediaAssetLengthMap = {
  [K in keyof typeof CommercialType]: number[];
};

export type ProjectWizardProps = {
  close: () => void;
};

export const ProjectWizard = observer(({ close }: ProjectWizardProps) => {
  /** Context **/
  const userStore = useUserStore();
  const projectStore = useProjectStore();
  const sessionStore = useSessionStore();
  const commercialStore = useCommercialStore();
  const roleStore = useRoleStore();

  /** State **/
  const [activeSlide, setActiveSlide] = useState(0);
  const [showNext, setShowNext] = useState(true);
  const [showPrevious, setShowPrevious] = useState(false);

  const [union, setUnion] = useState<string | undefined>();
  const [geography, setShoot] = useState<string | undefined>();
  const [place, setPlace] = useState<IAddress | undefined>();
  const [locationType, setLocationType] = useState<LocationType | undefined>();
  const [firstSessionDate, setFirstSessionDate] = useState<string>(toDateOnly(new Date()));

  const [code, setCode] = useState<string>('');
  const [media, setMedia] = useState<CommercialType[]>();
  const [assetCounts, setAssetCounts] = useState<IMediaAssetLengthMap>({} as IMediaAssetLengthMap);
  const [rolePerformers, setRolePerformers] = useState<IRolePerformer[]>([defaultRolePerformer]);
  const [exclusivity, setExclusivity] = useState<string | undefined>();
  const [clientList, setClientList] = useState<Partial<Advertiser>[]>([]);
  const [name, setName] = useState<string>('');
  const [client, setClient] = useState<Partial<Advertiser> | undefined>();
  const [product, setProduct] = useState<string>('');
  const [budget, setBudget] = useState<number>(0);
  const [showLoader, setShowLoader] = useState(false);

  /** Computed **/
  const mediaOptions = Object.keys(CommercialType).map((key) => CommercialType[key as keyof typeof CommercialType]);

  /** Methods **/

  const createProject = async () => {
    if (!name || !client || !product) {
      toast.error('Please fill out all fields');
      return;
    }
    if (!userStore.user.active_group) {
      toast.error('An internal error has occurred. Please refresh the page and try again.');
      return;
    }
    setShowLoader(true);

    let projectClient;
    // if client is new, create the client first
    if (client.id === 'NEW') {
      const newClient = await projectStore.createClient({
        name: client.name || '',
        isSagSignatory: client.isSagSignatory || false,
        isJpcAuthorizer: client.isJpcAuthorizer || false,
      });
      if (!newClient) {
        setShowLoader(false);
        toast.error('An internal error has occurred. Please refresh the page and try again.');
        return;
      }
      projectClient = newClient;
    } else {
      projectClient = client as Advertiser;
    }
    const project = await projectStore.createProject({
      name,
      groupId: userStore.user.active_group?.id,
      clientId: projectClient.id,
      product,
      jobCode: code,
      is_union: union === 'union',
      talentBudgetInPennies: budget ? budget * 100 : 0,
    });
    if (!project) {
      setShowLoader(false);
      toast.error('Project creation failed. Please refresh the page and try again.');
      return;
    }

    const locationRequest = {
      name: place?.street_address || place?.city || place?.state || `${locationType || LocationType.Nearby} Location`,
      type: locationType || LocationType.Nearby,
      street_address: place?.street_address || '',
      aptOrUnit: place?.aptOrUnit || '',
      city: place?.city || '',
      state: place?.state || '',
      zip: place?.zip || '',
      lat: place?.lat || 0,
      lng: place?.lng || 0,
      projectId: project.id,
    };
    try {
      place && (await projectStore.createLocation(locationRequest));

      firstSessionDate && (await sessionStore.createSession(new Date(firstSessionDate).toISOString(), project.id));
      for (const medium in assetCounts) {
        for (const length of assetCounts[medium as keyof typeof CommercialType]) {
          await commercialStore.createCommercial({
            projectId: project.id,
            title: `${length} second ${medium} asset`,
            type: medium as CommercialType,
            durationInSeconds: length,
            jobCode: code,
          });
        }
      }
      for (const rolePerformer of rolePerformers) {
        const roleIds = [];
        for (let i = 1; i <= rolePerformer.groupSize; i++) {
          const role = await roleStore.createRole({
            name: formatDefaultName(rolePerformer, i),
            type: rolePerformer.roleType,
            performerType: rolePerformer.performerType,
            projectId: project.id,
          });
          if (!role) throw new Error('Role creation failed');
          roleIds.push(role.id);
        }

        if (rolePerformer.groupSize > 1) {
          const roleGroup = await roleStore.createRoleGroup({
            name: formatDefaultName(rolePerformer),
            roleIds,
            projectId: project.id,
          });
          if (!roleGroup) throw new Error('Role group creation failed');
        }
      }
    } catch (e) {
      toast.error(`An internal error has occurred: ${e}`);
    }
    setClient(undefined);
    setName('');
    setProduct('');
    setCode('');
    setUnion(undefined);
    setBudget(0);
    setShoot(undefined);
    setLocationType(undefined);
    setPlace(undefined);
    setFirstSessionDate(new Date().toISOString());
    setExclusivity(undefined);
    setMedia(undefined);
    setAssetCounts({} as IMediaAssetLengthMap);
    setRolePerformers([defaultRolePerformer]);
    setActiveSlide(0);
    setShowLoader(false);
    close();
  };

  const valid = () => {
    return name && client && product ? true : false;
  };

  const onChild = (index: number) => {
    if (index < 0 || index > STEPS) {
      return;
    }
    if (index === 0) {
      setShowPrevious(false);
    } else {
      setShowPrevious(true);
    }
    if (index === STEPS) {
      setShowNext(false);
    } else {
      setShowNext(true);
    }
    setActiveSlide(index);
  };

  /** Effects **/

  useEffect(() => {
    if (projectStore.clientList.length === 0) return;
    setClientList(projectStore.clientList);
  }, [projectStore.clientList]);

  /** Render **/

  return (
    <Layer onEsc={close} onClickOutside={close} position="center" modal background="transparent">
      {showLoader ? (
        <Loader />
      ) : (
        <Card width="large" elevation="medium" height="90vh" round={borderRadiusSize}>
          <CardHeader
            gap="small"
            background={{ color: 'brand-gradient', dark: true }}
            style={{ background: 'var(--brand-gradient)', zIndex: 1 }}
            height="60px"
            justify="between"
            pad={{ horizontal: 'medium' }}
            elevation="small"
          >
            <Box direction="row" gap="small" align="center">
              <Text truncate size="large" color="white">
                Pre-Pro Talent Wizard
              </Text>
            </Box>
            <Button icon={<Close size="medium" color="white" />} onClick={close} />
          </CardHeader>
          <Box overflow={{ horizontal: 'auto', vertical: 'scroll' }} height="large" background="white">
            <Tabs activeIndex={activeSlide} onActive={onChild}>
              <Tab
                icon={<Magic />}
                title={
                  <Text size="small" weight={500}>
                    Start
                  </Text>
                }
                style={{ flex: 1 }}
              >
                <StartTab
                  name={name}
                  code={code}
                  client={client}
                  product={product}
                  budget={budget}
                  clientList={clientList}
                  setName={setName}
                  setCode={setCode}
                  setClient={setClient}
                  setClientList={setClientList}
                  setProduct={setProduct}
                  setBudget={setBudget}
                />
              </Tab>
              <Tab
                icon={<Globe />}
                title={<Text size="small">Shoot</Text>}
                style={{ flex: 1 }}
                // disabled={!valid()}
              >
                <ShootTab
                  geography={geography}
                  place={place}
                  setPlace={setPlace}
                  union={union}
                  firstSessionDate={firstSessionDate}
                  locationType={locationType}
                  setFirstSessionDate={setFirstSessionDate}
                  setShoot={setShoot}
                  setUnion={setUnion}
                  setLocationType={setLocationType}
                />
              </Tab>
              <Tab
                icon={<Multimedia />}
                title={<Text size="small">Media</Text>}
                style={{ flex: 1 }}
                disabled={!valid()}
              >
                <MediaTab
                  media={media}
                  setMedia={setMedia}
                  mediaOptions={mediaOptions}
                  exclusivity={exclusivity}
                  setExclusivity={setExclusivity}
                />
              </Tab>
              <Tab icon={<Channel />} title={<Text size="small">Assets</Text>} style={{ flex: 1 }} disabled={!valid()}>
                <AssetsTab media={media} setAssetCounts={setAssetCounts} assetCounts={assetCounts} />
              </Tab>
              <Tab icon={<User />} title={<Text size="small">Roles</Text>} style={{ flex: 1 }} disabled={!valid()}>
                <RolePerformersTab setRolePerformers={setRolePerformers} rolePerformers={rolePerformers} />
              </Tab>
              <Tab icon={<Deploy />} title={<Text size="small">Summary</Text>} style={{ flex: 1 }} disabled={!valid()}>
                <SummaryTab
                  name={name}
                  code={code}
                  client={client}
                  product={product}
                  budget={budget}
                  union={union}
                  geography={geography}
                  place={place}
                  media={media}
                  exclusivity={exclusivity}
                  assetCounts={assetCounts}
                  rolePerformers={rolePerformers}
                />
              </Tab>
            </Tabs>
          </Box>
          <Box
            direction="column"
            justify="stretch"
            flex="grow"
            background={{ color: 'light-4' }}
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
          >
            <ProjectWizardFooter
              activeSlide={activeSlide}
              setActiveSlide={onChild}
              previous={showPrevious}
              next={showNext}
              createFn={createProject}
              valid={valid}
            />
          </Box>
        </Card>
      )}
    </Layer>
  );
});
